import {
    type PurchasesPackage,
    LOG_LEVEL,
    PURCHASES_ERROR_CODE,
    Purchases,
} from '@revenuecat/purchases-capacitor';
import type {UserId} from '$lib/classes/data/_primitives';
import {logInProduction} from '$lib/utils/logInProduction';
import type {MobileSubscriptionPrices} from '$lib/classes/_types/mobile-subscription-prices';
import type {ToastManager} from '$lib/classes/stores/toast-manager';
import type {Database} from '$lib/classes/services/database';
import {writable} from 'svelte/store';

enum EventType {
    InitialPurchase = 'INITIAL_PURCHASE',
    Renewal = 'RENEWAL',
    Cancellation = 'CANCELLATION',
    Expiration = 'EXPIRATION',
}

export type MobileSubscriptionJson = {
    lastEventOccurredAt: number
    lastEventType: EventType
    purchased: number
    expires: number
}

export class MobileSubscriptionManager {
    private readonly STOCK_KEEPING_UNIT = 'vocabeo_pro_v1'
    private readonly PACKAGE = 'com.vocabeo'
    private readonly PUBLIC_API_KEY = 'goog_BRXoxonrlMFbcEWnBDrsGNGolkY'
    private readonly ENTITLEMENT_IDENTIFIER = 'pro';

    private readonly isNativeProductionApp

    private cancelDocSubscription = () => {}

    private _mobileSubscriptionPrices: MobileSubscriptionPrices = $state({});
    get mobileSubscriptionPrices() { return this._mobileSubscriptionPrices; }

    private _mobileSubscription = $state<MobileSubscriptionJson | null>(null);
    get mobileSubscription(): MobileSubscriptionJson | null {return this._mobileSubscription}

    public readonly hasActiveMobileSubscription = writable(false); //do not derive on mobile subscription but handle seperately for direct activation after purchase
    subscribe = this.hasActiveMobileSubscription.subscribe

    constructor(
        private readonly platform: string,
        private readonly appId: string,
        private readonly database: Database,
        private readonly toastManager: ToastManager,
    ) {
        this.isNativeProductionApp = this.platform !== 'web' && this.appId === this.PACKAGE
    }

    async init() {
        if (!this.isNativeProductionApp) return
        await Purchases.setLogLevel({level: LOG_LEVEL.DEBUG});
        if (this.platform === 'android') {
            await Purchases.configure({apiKey: this.PUBLIC_API_KEY});
        }
    }

    connect(uid: UserId): Promise<void> {
        return new Promise(async (resolve, reject) => {
            try {
                // subscribe to firestore doc
                this.cancelDocSubscription = await this.database.subscribeMobileSubscriptionDoc(uid, doc => {
                    if (doc) {
                        this._mobileSubscription = doc
                        // this.hasActiveMobileSubscription = true
                        const hasActiveMobileSubscription = doc.lastEventType !== EventType.Expiration
                        this.hasActiveMobileSubscription.set(hasActiveMobileSubscription)
                    } else {
                        this._mobileSubscription = null
                        // this.hasActiveMobileSubscription = false
                        this.hasActiveMobileSubscription.set(false)
                    }
                    resolve()
                })

                // authenticate with RevenueCat
                if (!this.isNativeProductionApp) return
                await Purchases.logIn({appUserID: uid});

            } catch (error) {
                reject(new Error('ERROR authenticating with MobileSubscriptionManager', {cause: error}));
            }
        })
    }

    async disconnect() {
        try {
            this.cancelDocSubscription()
            this.cancelDocSubscription = () => {}

            // reset state
            this._mobileSubscriptionPrices = {}
            this._mobileSubscription = null
            this.hasActiveMobileSubscription.set(false)

            if (!this.isNativeProductionApp) return
            const isConfigured = await Purchases.isConfigured();
            if (!isConfigured) return
            const isAnonymous = await Purchases.isAnonymous();
            if (isAnonymous) return
            await Purchases.logOut();
        } catch (error) {
            throw new Error('ERROR revoking authorization with MobileSubscriptionManger', {cause: error});
        }
    }

    async loadPrices() {
        if (!this.isNativeProductionApp) return
        try {
            const offerings = await Purchases.getOfferings();
            const currentOfferings = offerings.current
            this.mobileSubscriptionPrices.monthly = currentOfferings?.monthly ?? undefined
            logInProduction('monthly package', this.mobileSubscriptionPrices.monthly);
        } catch (error) {
            console.error('ERROR loading mobile subscription prices', error)
            this.toastManager.addErrorLoadingMobileSubscriptionPricesToast()
        }
    }

    async purchase(packageToBuy: PurchasesPackage) {
        if (!this.isNativeProductionApp) return
        try {
            const purchaseResult = await Purchases.purchasePackage({aPackage: packageToBuy});
            const customerInfo = purchaseResult.customerInfo;
            const isEntitlementActive = typeof customerInfo.entitlements.active[this.ENTITLEMENT_IDENTIFIER] !== 'undefined'
            this.hasActiveMobileSubscription.set(isEntitlementActive)
            // this.hasActiveMobileSubscription.current = isEntitlementActive // Set directly before webhook > firestore doc is created
            logInProduction('customerInfo.managementUrl', customerInfo.managementURL);
        } catch (error: any) {
            if (error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
                console.error('ERROR purchasing mobile subscription - purchase cancelled', error)
            } else if (error.code === PURCHASES_ERROR_CODE.PRODUCT_ALREADY_PURCHASED_ERROR) {
                console.error(`ERROR purchasing mobile subscription - already purchased - CHECK - if everything's working fine`, error)
            } else {
                console.error('ERROR purchasing mobile subscription', error)
                this.toastManager.addErrorPurchasingMobileSubscriptionToast()
            }
        }
    }

    manageMobileSubscription() {
        window.open(`https://play.google.com/store/account/subscriptions?sku=${this.STOCK_KEEPING_UNIT}&package=${this.PACKAGE}`, '_blank');
    }
}